<template>
  <article class="article-detail">
    <template v-if="article">
      <div class="article-detail-head" :class="articleSimpleClass">
        <breadcrumb :data="breadcrumbData"></breadcrumb>
        <h2 class="article-detail-title ellipsis-2" :title="article.post_title">
          {{ article.post_title }}
        </h2>
        <div class="article-detail-study">
          <span>{{ article.published_time | YYYYMMDD }}</span>
          <span>{{ article.post_hits | crowd }}人已阅读</span>
          <div class="article-detail-panel">
            <fav-btn
              :id="this.id"
              :isFav="isFav"
              @change="handleFav"
              @click.native="reportClick({ click: 'collect' })"
            >
              <div class="article-detail-panel-collect">
                <icon
                  :type="`iconic_collect${
                    isFav && this.$store.state.user.userInfo ? '_sel' : ''
                  }`"
                  class="article-detail-panel-icon"
                ></icon>
                <span class="collect-count">
                  {{ article.post_favorites | crowd }}</span
                >
              </div>
            </fav-btn>
            <like-btn
              :id="this.id"
              :isLike="isLike"
              @change="handleLike"
              @click.native="reportClick({ click: 'praise' })"
            >
              <div class="article-detail-panel-thumbs-up">
                <icon
                  :type="`iconic_like${
                    isLike && this.$store.state.user.userInfo ? '_sel' : ''
                  }`"
                  class="article-detail-panel-icon"
                ></icon>
                <span class="thumb-up-count">{{
                  article.post_like | crowd
                }}</span>
              </div>
            </like-btn>
          </div>
        </div>
      </div>

      <div class="article-detail-content" v-html="article.post_content" :class="articleSimpleClass"></div>
      <aside class="article-detail-sidebar" v-if="!simpleArticle">
        <hot-question
          :list="hotQuestion"
          v-if="hotQuestion.length > 0"
          @content-click="() => this.reportClick({ click: 'hot' })"
        ></hot-question>
      </aside>

      <feedback-help
        :id="Number(id)"
        :from-channel="3"
        sort="qa"
        area="bottom_right"
      ></feedback-help>
    </template>

    <empty v-else></empty>
  </article>
</template>

<script>
import FeedbackHelp from '@/components/common/feedback-help';
import Empty from '@/components/page-empty';
import HotQuestion from './components/hot-question';
import FavBtn from '@/components/common-btns/fav-btn';
import LikeBtn from '@/components/common-btns/like-btn';

import mixinAsyncData from '@/common/mixin/mixin-async-data';
import { FOOTER_HEIGHT } from '@/common/constant';
import { debounce } from '@/common/utils';

async function asyncData({ params, app , route }) {
  const id = params.id ? +params.id : '';
  const { path } = route;
  const isPreview = path.includes('preview');

  const [articleRes, hotQuestionRes] = await Promise.all([
    app.$service.learning.getQuestionDetail(id, isPreview),
    app.$service.learning.hotQuestionDetail(id),
  ]).catch(() => (null, null));

  const article = (articleRes && articleRes.data) || null;

  const hotQuestion = (hotQuestionRes && hotQuestionRes.data?.hot_qas) || [];

  let breadcrumbData;

  if (article) {
    // 获取面包屑
    const breadcrumbRes = await app.$service.learning
      .getBreadcrumb(article.post_type, id)
      .catch(() => {});

    breadcrumbData = (breadcrumbRes && breadcrumbRes.data) || [];
  }

  return {
    article,
    id,
    breadcrumbData,
    hotQuestion,
  };
}

export default {
  head() {
    let header = this.$head.defaultHead();

    const { article } = this;
    const siteInfo = this.$store.state.common.siteInfo;

    const title = `${(article && article.post_title) || ''}-WPS学堂`;
    const keywords = `${article && article.post_keywords},${
      siteInfo.site_seo_keywords
    }`;
    const description = `${article && article.post_excerpt},${
      siteInfo.site_seo_description
    }`;

    let options = {
      title,
      keywords,
      description,
    };

    if (article) {
      header = this.$head.detailHead(options);
    }

    return header;
  },

  data() {
    return {
      isLike: 0,
      isFav: 0,

      article: null,
      id: 0,
      breadcrumbData: [],
      hotQuestion: [],

      articleProgress: 0,
    };
  },

  mixins: [mixinAsyncData(asyncData)],

  components: {
    Empty,
    FeedbackHelp,
    HotQuestion,
    FavBtn,
    LikeBtn,
  },

  computed: {
    simpleArticle() { // 简洁版simple页面
      return this.$route.path.split('/').indexOf('simple') > -1
    },
    articleSimpleClass() {
      return {
        'is-simple-article': this.simpleArticle
      }
    }
  },

  methods: {
    /**
     * 处理收藏
     */
    handleFav(status) {
      this.isFav = status;

      if (status) {
        this.article.post_favorites += 1;
      } else {
        this.article.post_favorites -= 1;

        //避免因为页面缓存出现0-1=-1的状况
        if (this.article.post_favorites < 0) {
          this.article.post_favorites = 0;
        }
      }
    },

    /**
     * 处理点赞
     */
    handleLike(status) {
      this.isLike = status;
      if (status) {
        this.article.post_like = +this.article.post_like + 1;
      } else {
        this.article.post_like = +this.article.post_like - 1;
      }

      //避免因为页面缓存出现-1的状况
      if (this.article.post_like < 0) {
        this.article.post_like = 0;
      }
    },
    /**
     * 获取点赞、收藏状态
     */
    async getUserState() {
      const likeFavRes = await this.$service.learning
        .getLikeFavState(this.id)
        .catch(() => {});

      if (likeFavRes && likeFavRes.data) {
        this.isLike = likeFavRes.data.is_like;
        this.isFav = likeFavRes.data.is_fav;
      }
    },

    /**
     * 上报点击事件的方法
     */
    reportClick({ click }) {
      this.$collect.detailsQaClick({
        click,
        type: this.id,
      });
    },

    /**
     * 上报展示事件的方法
     */
    reportShow({ show }) {
      this.$collect.detailsQaShow({
        show,
        type: this.id,
      });
    },

    /**
     * 滚动触底事件
     */
    isScrollBottom() {
      // 页面底部计算
      const { bottom } = document.body.getBoundingClientRect();
      const { innerHeight } = window;

      if (bottom - innerHeight <= FOOTER_HEIGHT) {
        this._isScrollBottom = true;
      }

      // 问答浏览进度计算
      const articleContainer = document.getElementsByClassName('article-detail')[0];
      if (articleContainer) {
        const { top, height } = articleContainer.getBoundingClientRect();
        const scrollTop = document.documentElement.scrollTop;
        const clientHeight = document.documentElement.clientHeight;

        // 上报问答阅读进度
        let currentProgress = Math.round(((clientHeight + scrollTop) - (top + scrollTop)) / height * 100) / 100;
        if (currentProgress > 0 && this.articleProgress < currentProgress) {
          let process = 0;
          if (currentProgress > 0.3 && currentProgress < 0.5) {
            process = 0.3
          } else if (currentProgress >= 0.5 && currentProgress < 0.9) {
            process = 0.5
          } else if (currentProgress >= 0.9 && currentProgress < 1) {
            process = 0.9
          } else if (currentProgress >= 1) {
            process = 1
          }

          if (this.articleProgress < process) {
            this.reportShow({ show: `${process}` })
            this.articleProgress = process ;
          }
        }
      }
    },

    afterMounted() {
      const { article } = this;

      // 记录进入时长
      this._enterTime = Date.now();

      if (article) {
        this.tab =
          article.more && article.more.privilege_show === 'anli'
            ? 'case'
            : 'content';

        this.getUserState();

        // 记录访问
        this.$service.learning.readPostItem(this.article.post_type, this.id);
      }

      // 上报详情页展示
      this.reportShow({ show: 'details' });

      // 监听滚动到底部的事件
      this.debounceisScrollBottom = debounce(this.isScrollBottom, 300);
      window.addEventListener('scroll', this.isScrollBottom);

      // 上报页面结束
      window.addEventListener('beforeunload', () => {
        if (this._isBeforeUnload) return;
        this._isBeforeUnload = true;
        this.$collect.detailsQaEnd({
          view: this._isScrollBottom ? 'yes' : 'no',
          time: Math.ceil((Date.now() - this._enterTime) / 1000),
          type: this.id,
        });
      });
    },
  },

  destroyed() {
    window.removeEventListener('scroll', this.debounceisScrollBottom);
  },
};
</script>

<style lang="scss" scoped>
.article-detail {
  width: 1110px;
  position: relative;

  &-title {
    font-size: 24px;
    text-align: left;
    width: 100%;
  }

  &-study {
    text-align: left;
    margin-top: 15px;
    width: 100%;
    clear: both;
    overflow: hidden;
    padding-bottom: 6px;
    border-bottom: 1px solid #e8e8e8;
    > span {
      font-size: 12px;
      color: #999;
      float: left;
      padding-right: 40px;
    }
  }

  &-panel {
    display: inline-block;
    background: none;
    margin: 0;
    width: auto;
    height: auto;
    position: relative;
    font-size: 12px;
    color: #999;
    top: -1px;
    &-collect {
      float: left;
      margin-right: 40px;
      cursor: pointer;
      > svg {
        color: rgb(247, 201, 64);
      }
    }
    &-thumbs-up {
      float: left;
      margin-right: 40px;
      cursor: pointer;
      > {
        float: none;
        padding-right: 0px;
      }
    }
    &-icon {
      width: 16px;
      height: 16px;
    }
  }

  &-head {
    width: 802px;
    text-align: left;
  }

  &-content {
    font-size: 16px;
    line-height: 1.5;
    margin-top: 20px;
    margin-bottom: 52px;
    overflow: hidden;
    clear: both;
    width: 802px;
    min-height: 300px;
  }

  &-sidebar {
    position: absolute;
    right: 0;
    top: 0;
    width: 290px;
    z-index: 8;
  }

  .is-simple-article {
    width: 100%;
  }
}
</style>

<style lang="scss">
.article-detail-content p span {
  font-family: '微软雅黑' !important;
}
</style>
